import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RecipeForm } from '@/components/recipe/recipe-form'
import { useRecipeStore } from '@/stores/recipe-store'
import { useAuthStore } from '@/stores/auth-store'
import { Recipe } from '@/types/recipe'

export default function EditRecipePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { loadRecipe, loading } = useRecipeStore()
  const { user } = useAuthStore()
  const [recipe, setRecipe] = useState<Recipe | null>(null)
  const [loadingRecipe, setLoadingRecipe] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!id) {
        setErrorMessage('Geen recept ID gevonden')
        setLoadingRecipe(false)
        return
      }

      try {
        setLoadingRecipe(true)
        const fetchedRecipe = await loadRecipe(id)
        
        if (!fetchedRecipe) {
          setErrorMessage('Recept niet gevonden')
          return
        }

        // Check if user owns this recipe
        if (fetchedRecipe.user_id !== user?.id) {
          setErrorMessage('Je kunt alleen je eigen recepten bewerken')
          return
        }

        setRecipe(fetchedRecipe)
        setErrorMessage(null)
      } catch (err) {
        console.error('Error loading recipe:', err)
        setErrorMessage('Er is een fout opgetreden bij het laden van het recept')
      } finally {
        setLoadingRecipe(false)
      }
    }

    fetchRecipe()
  }, [id, loadRecipe, user?.id])

  if (loadingRecipe || loading) {
    return (
      <div className="container py-8">
        <div className="mb-6">
          <Button variant="outline" asChild>
            <Link to={`/recepten/${id}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug naar Recept
            </Link>
          </Button>
        </div>
        
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Recept laden...</p>
        </div>
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="container py-8">
        <div className="mb-6">
          <Button variant="outline" asChild>
            <Link to={`/recepten/${id}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug naar Recept
            </Link>
          </Button>
        </div>
        
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-red-800 mb-2">
              Kan recept niet bewerken
            </h1>
            <p className="text-red-600 mb-4">
              {errorMessage}
            </p>
            <Button variant="outline" onClick={() => navigate('/recepten')}>
              Terug naar Recepten
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!recipe) {
    return null
  }

  return (
    <div>
      <RecipeForm recipe={recipe} mode="edit" />
    </div>
  )
}